import { useState } from "react";
import { HorseArt } from "@/components/game/HorseArt";
import { ArrowLeft, Dumbbell, User, Star, Tag, PlusCircle, Sparkles, Lock, Warehouse, PawPrint } from "lucide-react";

const place = (n) => (n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th");

const Header = ({ title, onBack }) => (
  <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
    <button data-testid="stall-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
    <Warehouse className="text-secondary" size={18} />
    <h2 className="font-display text-xl font-bold text-primary">{title}</h2>
  </div>
);

export const StallPage = ({ stall, player, onBack, onEnter, onSell, onProfile, onSetFavorite, onNewMakeover, onAssign, onCustomize, onUpgrades }) => {
  const [assigning, setAssigning] = useState(false);
  if (!stall) return null;

  const kept = (player?.alumni || []).filter((a) => a.decision === "keep" && a.id);
  const assignedIds = new Set(Object.values(player?.stall_assignments || {}));
  const unassigned = kept.filter((a) => !assignedIds.has(a.id));
  const horse = stall.horse;
  const full = horse ? kept.find((a) => a.id === horse.id) : null;

  if (stall.state === "unavailable") {
    return (
      <div className="pb-10" data-testid="stall-page">
        <Header title={`Stall ${stall.number}`} onBack={onBack} />
        <div className="mx-4 mt-6 rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center" data-testid="stall-unavailable">
          <Lock size={30} className="mx-auto text-muted-foreground/60" />
          <p className="mt-3 font-display text-lg font-bold text-primary">Stall Unavailable</p>
          <p className="mt-1 text-sm text-muted-foreground">This stall is part of a future stable area and isn't available yet.</p>
          <button data-testid="stall-return" onClick={onBack} className="mx-auto mt-4 h-11 w-full max-w-xs rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Return to Stable</button>
        </div>
      </div>
    );
  }

  if (stall.state === "empty") {
    return (
      <div className="pb-10" data-testid="stall-page">
        <Header title={`Stall ${stall.number}`} onBack={onBack} />
        <div className="mx-4 mt-4 rounded-2xl border border-border bg-card p-6 text-center" data-testid="stall-empty">
          <Sparkles size={28} className="mx-auto text-secondary/60" />
          <p className="mt-2 font-display text-lg font-bold text-primary">Empty Stall</p>
          <p className="mt-1 text-sm text-muted-foreground">Start a new makeover or move one of your kept horses into this stall.</p>
          <div className="mt-4 space-y-2">
            <button data-testid="stall-new-makeover" onClick={onNewMakeover} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary font-bold text-primary-foreground active:scale-95"><PlusCircle size={16} /> Start New Makeover</button>
            {unassigned.length > 0 && (
              <button data-testid="stall-assign-toggle" onClick={() => setAssigning((v) => !v)} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border-2 border-secondary font-bold text-secondary active:scale-95"><PawPrint size={16} /> Assign Existing Horse</button>
            )}
            <button data-testid="stall-customize" onClick={onCustomize} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border font-bold text-primary active:scale-95"><Sparkles size={16} /> Customize (Tack &amp; Barn Shop)</button>
            <button data-testid="stall-return" onClick={onBack} className="h-11 w-full rounded-xl border border-border font-bold text-primary active:scale-95">Return to Stable</button>
          </div>
          {assigning && (
            <div className="mt-3 space-y-2 rounded-xl border border-secondary/30 bg-secondary/5 p-3 text-left" data-testid="stall-assign-list">
              {unassigned.map((a) => (
                <button key={a.id} data-testid={`stall-assign-${a.id}`} onClick={() => onAssign(stall.number, a.id)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-2 text-left active:scale-[0.99]">
                  <div className="grid h-11 w-11 place-items-center rounded-lg" style={{ background: `${a.color_hex}1a` }}><HorseArt src={a.art_image} color={a.color_hex} size={40} /></div>
                  <div><p className="text-sm font-bold text-primary">{a.name}</p><p className="text-[11px] text-muted-foreground">Tag #{a.tag}{a.hma ? ` · ${a.hma} HMA` : ""}</p></div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // occupied
  const sc = full?.scores || {};
  return (
    <div className="pb-10" data-testid="stall-page">
      <Header title={horse?.name || `Stall ${stall.number}`} onBack={onBack} />
      <div className="mx-4 mt-4 rounded-2xl border border-border bg-card p-4" data-testid="stall-occupied">
        <div className="flex items-center gap-3">
          <div className="grid h-24 w-24 shrink-0 place-items-center rounded-xl" style={{ background: `${horse.color_hex}1a` }}><HorseArt src={horse.art_image} color={horse.color_hex} size={84} /></div>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 font-display text-2xl font-bold text-primary">{horse.name}{horse.is_favorite && <Star size={18} className="fill-amber-400 text-amber-400" data-testid="stall-fav-star" />}</p>
            <p className="text-xs font-semibold text-secondary">Tag #{horse.tag}{full?.hma ? ` · ${full.hma} HMA` : ""}</p>
            {sc.placing && <p className="text-xs text-muted-foreground">Placed {sc.placing}{place(sc.placing)} · Overall {sc.overall}</p>}
            <p className="mt-0.5 text-[11px] font-semibold text-muted-foreground">Stall {stall.number} · Kept</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid="stall-enter" onClick={() => onEnter(full)} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground active:scale-95"><Dumbbell size={15} /> Train &amp; Care</button>
          <button data-testid="stall-profile" onClick={() => onProfile(full)} className="flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-border text-sm font-bold text-primary active:scale-95"><User size={15} /> View Profile</button>
          <button data-testid="stall-favorite" onClick={() => onSetFavorite(horse)} className="flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-amber-400 text-sm font-bold text-amber-600 active:scale-95"><Star size={15} /> Set Favorite</button>
          <button data-testid="stall-customize-occ" onClick={onCustomize} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-border text-sm font-bold text-primary active:scale-95"><Sparkles size={15} /> Customize</button>
          <button data-testid="stall-sell" onClick={() => onSell(full)} className="col-span-2 flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-amber-500 text-sm font-bold text-amber-600 active:scale-95"><Tag size={15} /> Sell / Rehome</button>
        </div>
        <button data-testid="stall-return" onClick={onBack} className="mt-2 h-10 w-full rounded-xl border border-border text-sm font-bold text-primary active:scale-95">Return to Stable</button>
      </div>
    </div>
  );
};
